import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { Button, Flex, Typography } from 'antd';

import AccountDescriptions from './AccountDescriptions';
import ChangeInTermsModal from './ChangeInTermsModal';
import APR from './APR';
import useModalControls from 'hooks/useModalControls';
import { GET_CAG_BY_ACCOUNT_ID } from 'queries/cag';
import { UPCOMING_CIT_STATUS } from 'utilities/constants';
import { formatDate } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';

const { Text } = Typography;

const TermsDescriptions = ({ account, pricingDetails }) => {
  const changeInTermsModal = useModalControls();
  const { accountId } = useParams();

  const cagQuery = useQuery(GET_CAG_BY_ACCOUNT_ID, {
    variables: { accountId },
  });

  const [cagLink, cagFailure] = useMemo(() => {
    const cagLink =
      cagQuery.data?.bookedAccountsLatestCag?.documentLink || null;
    return [cagLink, !!cagQuery.error];
  }, [cagQuery.data, cagQuery.error]);

  const [upcomingChangeInTerms, upcomingChangeInTermsFailure] = useMemo(() => {
    const changeInTerms = account?.changeInTerms;
    let upcoming = false;
    let failure = changeInTerms === null;

    if (changeInTerms) {
      upcoming = changeInTerms.some((cit) => {
        return UPCOMING_CIT_STATUS.includes(cit.status);
      });
    }

    return [upcoming, failure];
  }, [account]);

  const getCagText = () => {
    if (cagFailure) {
      return <Text type="danger">Failed to retrieve</Text>;
    }
    return (
      <>
        <a target="_blank" rel="noreferrer" href={cagLink}>
          View CAG
        </a>
        {pricingDetails?.hasResidualInterest && <span>(RI)</span>}
      </>
    );
  };

  const items = [];

  items.push({
    key: 'credit-limit',
    span: 2,
    label: 'Credit Limit',
    children: (
      <span data-testid="credit-limit">
        {formatDollarsFromCents(pricingDetails?.creditLimit)}
      </span>
    ),
  });

  items.push({
    key: 'annual-fee',
    span: 2,
    label: 'Annual Fee',
    children: (
      <span data-testid="annual-fee">
        {formatDollarsFromCents(pricingDetails?.annualFee)}
      </span>
    ),
  });

  items.push({
    key: 'monthly-fee',
    span: 2,
    label: 'Monthly Fee',
    children: (
      <span data-testid="monthly-fee">
        {formatDollarsFromCents(pricingDetails?.monthlyFee)}
      </span>
    ),
  });

  if (pricingDetails?.previousMembershipFeeDate) {
    items.push({
      key: 'previous-fee-date',
      span: 2,
      label: 'Previous Fee Assessment',
      children: (
        <span data-testid="previous-fee-assessment">
          {formatDate(pricingDetails.previousMembershipFeeDate)}
        </span>
      ),
    });
  }

  if (pricingDetails?.nextMembershipFeeDate) {
    items.push({
      key: 'next-fee-assessment',
      span: 2,
      label: 'Next Fee Assessment',
      children: (
        <span data-testid="next-fee-assessment">
          {formatDate(pricingDetails.nextMembershipFeeDate)}
        </span>
      ),
    });
  }

  items.push({
    key: 'apr',
    span: 2,
    label: 'APR',
    children: <APR pricingDetails={pricingDetails} />,
  });

  items.push({
    key: 'cash-advance-limit',
    span: 2,
    label: 'Cash Advance Limit',
    children: (
      <span data-testid="cash-advance-limit">
        {formatDollarsFromCents(pricingDetails?.cashAdvanceCreditLimit)}
      </span>
    ),
  });

  items.push({
    key: 'available-cash-advance',
    span: 2,
    label: 'Available Cash Advance',
    children: (
      <span data-testid="available-cash-advance">
        {formatDollarsFromCents(
          account?.balanceInfo?.cashAdvanceAvailableCreditLimit,
        )}
      </span>
    ),
  });

  items.push({
    key: 'cash-advance-apr',
    span: 2,
    label: 'Cash Advance APR',
    children: (
      <span data-testid="cash-advance-apr">
        {pricingDetails?.cashAdvanceApr}%{' '}
        {pricingDetails?.cashAprType?.toLowerCase()}
      </span>
    ),
  });

  items.push({
    key: 'cag',
    span: 2,
    label: 'CAG',
    children: getCagText(),
  });

  items.push({
    key: 'upcoming-cit',
    span: 2,
    label: 'Upcoming CIT',
    children: (
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap="var(--spacing-xxs)"
      >
        <div data-testid="change-in-terms-status">
          {upcomingChangeInTermsFailure ? (
            <Text type="danger">Failed to retrieve</Text>
          ) : (
            <Text>{upcomingChangeInTerms ? 'Yes' : 'No'}</Text>
          )}
        </div>
        <Button
          type="link"
          size="small"
          onClick={changeInTermsModal.show}
          style={{ padding: 0 }}
        >
          View History
        </Button>
      </Flex>
    ),
  });

  return (
    <>
      <AccountDescriptions title="Terms" items={items} />
      <ChangeInTermsModal
        {...changeInTermsModal}
        changeInTerms={account?.changeInTerms}
      />
    </>
  );
};

export default TermsDescriptions;
